import axios from 'axios'

const DEFAULT_PARALLELISM = 3
const DEFAULT_MAX_RETRIES = 5
const BACKOFF_BASE_MS = 500
const BACKOFF_CAP_MS = 30000

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

function backoffDelay(attempt) {
  const exp = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt)
  return exp / 2 + Math.random() * (exp / 2) // jitter in [exp/2, exp)
}

function isAbortError(err) {
  return err?.name === 'AbortError' || err?.code === 'ERR_CANCELED' || axios.isCancel?.(err)
}

async function putChunkWithRetry({ uploadId, index, blob, signal, maxRetries }) {
  let lastErr
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
    try {
      await axios.put(`/api/uploads/${uploadId}/chunk/${index}`, blob, {
        headers: { 'Content-Type': 'application/octet-stream' },
        signal,
        // Send raw Blob bytes — let the browser set the content-length.
        // Disable axios's default JSON transform.
        transformRequest: [(data) => data],
      })
      return
    } catch (err) {
      if (isAbortError(err)) throw err
      lastErr = err

      // 409 / 410 are terminal: session already completed, aborted, or expired.
      const status = err?.response?.status
      if (status === 409 || status === 410 || status === 404 || status === 403) {
        throw err
      }

      if (attempt < maxRetries) {
        await sleep(backoffDelay(attempt))
      }
    }
  }
  throw lastErr
}

async function runWithConcurrency(items, concurrency, worker) {
  const queue = items.slice()
  const inFlight = []
  const failures = []

  while (queue.length > 0 || inFlight.length > 0) {
    while (queue.length > 0 && inFlight.length < concurrency) {
      const item = queue.shift()
      const p = worker(item).catch((err) => {
        failures.push(err)
        throw err
      })
      inFlight.push(p)
      p.finally(() => {
        const i = inFlight.indexOf(p)
        if (i >= 0) inFlight.splice(i, 1)
      })
    }
    if (inFlight.length > 0) {
      try {
        await Promise.race(inFlight)
      } catch {
        // Failure already captured; break out to surface it
        break
      }
    }
  }

  if (failures.length > 0) throw failures[0]
}

/**
 * Upload a File or Blob in chunks with parallelism, retry, and resume.
 *
 * @param {File|Blob} file
 * @param {object} [opts]
 * @param {(p: {loaded: number, total: number, percent: number}) => void} [opts.onProgress]
 * @param {AbortSignal} [opts.signal]
 * @param {number} [opts.parallelism=3]
 * @param {number} [opts.maxRetries=5]
 * @param {string} [opts.existingUploadId] - resume an existing session instead of init
 *
 * @returns {Promise<{uploadId: string, url: string, size: number, filename: string, mimetype: string}>}
 */
export async function uploadFileInChunks(file, opts = {}) {
  if (!file || typeof file.size !== 'number') {
    throw new Error('uploadFileInChunks: file is required')
  }

  const {
    onProgress,
    signal,
    parallelism = DEFAULT_PARALLELISM,
    maxRetries = DEFAULT_MAX_RETRIES,
    existingUploadId,
  } = opts

  // --- 1. Init or resume ---
  let session
  if (existingUploadId) {
    const { data } = await axios.get(`/api/uploads/${existingUploadId}/status`, { signal })
    session = {
      uploadId: data.uploadId,
      chunkSize: file.size, // unknown until we re-init; only used for sizing — see note below
      totalChunks: data.totalChunks,
      receivedChunks: data.receivedChunks || [],
      status: data.status,
      finalUrl: data.finalUrl,
    }
    if (session.status === 'completed') {
      onProgress?.({ loaded: file.size, total: file.size, percent: 100 })
      return {
        uploadId: session.uploadId,
        url: session.finalUrl,
        size: file.size,
        filename: file.name,
        mimetype: file.type,
      }
    }
    // For resume we need chunkSize too — re-init isn't possible, so this branch
    // assumes the caller persists chunkSize itself OR we re-init below.
    // For simplicity, fall through to a fresh init if chunkSize is unknown.
  }

  if (!session || !session.chunkSize || session.chunkSize === file.size) {
    const { data } = await axios.post(
      '/api/uploads/init',
      {
        filename: file.name || 'file',
        mimetype: file.type || 'application/octet-stream',
        totalSize: file.size,
      },
      { signal }
    )
    session = {
      uploadId: data.uploadId,
      chunkSize: data.chunkSize,
      totalChunks: data.totalChunks,
      receivedChunks: [],
      status: 'pending',
    }
  }

  // --- 2. Build chunk plan ---
  const pending = []
  for (let i = 0; i < session.totalChunks; i++) {
    if (!session.receivedChunks.includes(i)) pending.push(i)
  }

  // --- 3. Progress tracking ---
  let bytesUploaded = session.receivedChunks.reduce((sum, i) => {
    const isLast = i === session.totalChunks - 1
    return sum + (isLast ? file.size - i * session.chunkSize : session.chunkSize)
  }, 0)
  const emitProgress = () => {
    onProgress?.({
      loaded: bytesUploaded,
      total: file.size,
      percent: file.size === 0 ? 100 : Math.min(100, (bytesUploaded / file.size) * 100),
    })
  }
  emitProgress()

  // --- 4. Upload chunks ---
  const work = pending.map((index) => async () => {
    const start = index * session.chunkSize
    const end = Math.min(file.size, start + session.chunkSize)
    const blob = file.slice(start, end)
    await putChunkWithRetry({
      uploadId: session.uploadId,
      index,
      blob,
      signal,
      maxRetries,
    })
    bytesUploaded += end - start
    emitProgress()
  })

  await runWithConcurrency(work, parallelism, (fn) => fn())

  // --- 5. Complete (server enqueues a worker thread, returns 202 'processing') ---
  const { data: complete } = await axios.post(
    `/api/uploads/${session.uploadId}/complete`,
    {},
    { signal }
  )

  if (complete.status === 'completed' && complete.url) {
    return {
      uploadId: complete.uploadId,
      url: complete.url,
      size: complete.size,
      filename: complete.filename,
      mimetype: complete.mimetype,
    }
  }

  // --- 6. Poll until worker finishes ---
  const finalized = await pollUntilProcessed(session.uploadId, { signal })
  return {
    uploadId: finalized.uploadId,
    url: finalized.finalUrl,
    size: Number(finalized.size) || file.size,
    filename: finalized.filename || file.name,
    mimetype: finalized.mimetype || file.type,
  }
}

async function pollUntilProcessed(uploadId, { signal, intervalMs = 1500, timeoutMs = 10 * 60 * 1000 } = {}) {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new Error('Upload aborted')
    const { data } = await axios.get(`/api/uploads/${uploadId}/status`, { signal })
    if (data.status === 'completed') return data
    if (data.status === 'failed') {
      throw new Error(data.errorMessage || 'Upload processing failed on server')
    }
    if (data.status === 'aborted') {
      throw new Error('Upload session was aborted')
    }
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  throw new Error('Timed out waiting for server to finalize upload')
}

/**
 * Best-effort abort + cleanup. Call this if a user cancels mid-upload.
 */
export async function abortUpload(uploadId) {
  try {
    await axios.delete(`/api/uploads/${uploadId}`)
  } catch {
    // Best-effort; the server's cleanup cron will reap orphans.
  }
}
